import { useCallback, useEffect, useMemo, useState } from 'react';
import { advisoryV3TurnPath, listenToAdvisoryV3Turn } from '../../services/advisoryService';
import {
  mapAdvisoryResearchContext,
  type AdvisoryResearchContextV01,
} from '../../advisory/composites/ui/advisoryProvenance';

export type AdvisoryResearchContextStatus =
  | 'disabled'
  | 'loading'
  | 'ready'
  | 'absent'
  | 'denied'
  | 'failed'
  | 'invalid';

export interface UseAdvisoryResearchContextOptions {
  enabled: boolean;
  userId?: string;
  /** Persisted S-05 root turn only; never derived from a display label or SSE text. */
  rootTurnId?: string;
}

export interface UseAdvisoryResearchContextResult {
  status: AdvisoryResearchContextStatus;
  context: AdvisoryResearchContextV01 | null;
  errorCode: string | null;
  retry: () => void;
}

interface ResearchContextState {
  status: AdvisoryResearchContextStatus;
  context: AdvisoryResearchContextV01 | null;
  errorCode: string | null;
}

const DISABLED: ResearchContextState = {
  status: 'disabled',
  context: null,
  errorCode: null,
};

const isPermissionDenied = (code: string): boolean =>
  code === 'permission-denied' || code === 'firestore/permission-denied';

/**
 * Reload-safe, read-only access to the completed S-05 root turn's compact
 * provenance roll-up. This hook never follows V3 child nodes or writes data.
 */
export function useAdvisoryResearchContext({
  enabled,
  userId,
  rootTurnId,
}: UseAdvisoryResearchContextOptions): UseAdvisoryResearchContextResult {
  const [state, setState] = useState<ResearchContextState>(DISABLED);
  const [retryVersion, setRetryVersion] = useState(0);
  const path = useMemo(
    () => (enabled && userId && rootTurnId ? advisoryV3TurnPath(userId, rootTurnId) : null),
    [enabled, rootTurnId, userId]
  );

  useEffect(() => {
    if (!enabled || !path) {
      setState(DISABLED);
      return undefined;
    }
    let live = true;
    setState({ status: 'loading', context: null, errorCode: null });
    const subscription = listenToAdvisoryV3Turn(
      path,
      document => {
        if (!live) return;
        if (!document) {
          setState({ status: 'absent', context: null, errorCode: null });
          return;
        }
        const mapped = mapAdvisoryResearchContext(document);
        setState(
          mapped.ok
            ? { status: 'ready', context: mapped.value, errorCode: null }
            : { status: 'invalid', context: null, errorCode: null }
        );
      },
      errorCode => {
        if (!live) return;
        setState({
          status: isPermissionDenied(errorCode) ? 'denied' : 'failed',
          context: null,
          errorCode,
        });
      }
    );
    const unsubscribe = typeof subscription === 'function' ? subscription : () => undefined;
    return () => {
      live = false;
      unsubscribe();
    };
  }, [enabled, path, retryVersion]);

  const retry = useCallback(() => setRetryVersion(version => version + 1), []);
  return { ...state, retry };
}
